/**
 * Firebase Authentication Service
 * Handles user authentication operations for the nursing home costing system
 */

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  updateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  User as FirebaseUser,
  UserCredential,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { User } from '@/types';

/**
 * Sign in user with email and password
 */
export const signIn = async (
  email: string,
  password: string
): Promise<UserCredential> => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );

    // Update last login timestamp
    if (userCredential.user) {
      await updateLastLogin(userCredential.user.uid);
    }

    return userCredential;
  } catch (error) {
    console.error('Sign in error:', error);
    throw error;
  }
};

/**
 * Register a new user (creates user with pending status)
 */
export const registerUser = async (
  email: string,
  password: string,
  displayName: string
): Promise<UserCredential> => {
  try {
    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    // Update profile with display name
    await updateProfile(userCredential.user, { displayName });

    // Create user document in Firestore with pending status
    await createUserDocument(userCredential.user, displayName);

    return userCredential;
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

/**
 * Sign out current user
 */
export const signOutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
};

/**
 * Send password reset email
 */
export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Password reset error:', error);
    throw error;
  }
};

/**
 * Create user document in Firestore
 */
export const createUserDocument = async (
  firebaseUser: FirebaseUser,
  displayName: string
): Promise<void> => {
  const userDoc: Omit<User, 'id'> = {
    email: firebaseUser.email!,
    displayName,
    status: 'pending',
    createdAt: new Date(),
    role: 'super_admin',
  };

  await setDoc(doc(db, 'users', firebaseUser.uid), {
    ...userDoc,
    createdAt: serverTimestamp(),
  });
};

/**
 * Get user document from Firestore
 */
export const getUserDocument = async (uid: string): Promise<User | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      return {
        id: userDoc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        approvedAt: data.approvedAt?.toDate(),
        lastLoginAt: data.lastLoginAt?.toDate(),
      } as User;
    }
    return null;
  } catch (error) {
    console.error('Error getting user document:', error);
    throw error;
  }
};

/**
 * Update user's last login timestamp
 */
export const updateLastLogin = async (uid: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', uid), {
      lastLoginAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating last login:', error);
    // Don't throw here as this is not critical
  }
};

/**
 * Approve a user (admin function)
 */
export const approveUser = async (
  uid: string,
  approvedBy: string
): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', uid), {
      status: 'approved',
      approvedAt: serverTimestamp(),
      approvedBy,
    });
  } catch (error) {
    console.error('Error approving user:', error);
    throw error;
  }
};

/**
 * Suspend a user (admin function)
 */
export const suspendUser = async (uid: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', uid), {
      status: 'suspended',
    });
  } catch (error) {
    console.error('Error suspending user:', error);
    throw error;
  }
};

/**
 * Get all pending users (admin function)
 */
export const getPendingUsers = async (): Promise<User[]> => {
  try {
    const q = query(collection(db, 'users'), where('status', '==', 'pending'));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      approvedAt: doc.data().approvedAt?.toDate(),
      lastLoginAt: doc.data().lastLoginAt?.toDate(),
    })) as User[];
  } catch (error) {
    console.error('Error getting pending users:', error);
    throw error;
  }
};

/**
 * Get all users (admin function)
 */
export const getAllUsers = async (): Promise<User[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'users'));

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      approvedAt: doc.data().approvedAt?.toDate(),
      lastLoginAt: doc.data().lastLoginAt?.toDate(),
    })) as User[];
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

/**
 * Update user profile information (admin function)
 */
export const updateUserProfile = async (
  uid: string,
  updates: {
    displayName?: string;
    email?: string;
  }
): Promise<void> => {
  try {
    // Update the Firestore document
    const updateData: any = {};
    if (updates.displayName) updateData.displayName = updates.displayName;
    if (updates.email) updateData.email = updates.email;

    if (Object.keys(updateData).length > 0) {
      await updateDoc(doc(db, 'users', uid), updateData);
    }
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Update current user's own profile
 */
export const updateCurrentUserProfile = async (updates: {
  displayName?: string;
  email?: string;
}): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user found');
  }

  try {
    // Update Firebase Auth profile
    if (updates.displayName !== undefined) {
      await updateProfile(user, { displayName: updates.displayName });
    }

    // Update email if provided (requires recent authentication)
    if (updates.email && updates.email !== user.email) {
      await updateEmail(user, updates.email);
    }

    // Update Firestore document
    const updateData: any = {};
    if (updates.displayName !== undefined)
      updateData.displayName = updates.displayName;
    if (updates.email !== undefined) updateData.email = updates.email;

    if (Object.keys(updateData).length > 0) {
      await updateDoc(doc(db, 'users', user.uid), updateData);
    }
  } catch (error) {
    console.error('Error updating current user profile:', error);
    throw error;
  }
};

/**
 * Change current user's password
 */
export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error('No authenticated user found');
  }

  try {
    // Reauthenticate user with current password
    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword
    );
    await reauthenticateWithCredential(user, credential);

    // Update password
    await updatePassword(user, newPassword);
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
};

/**
 * Reauthenticate user with current password
 */
export const reauthenticateUser = async (
  currentPassword: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error('No authenticated user found');
  }

  try {
    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword
    );
    await reauthenticateWithCredential(user, credential);
  } catch (error) {
    console.error('Error reauthenticating user:', error);
    throw error;
  }
};
